/**
 * Synthetic connection reference for the Microsoft-managed
 * `AgentModelProvider.MicrosoftManaged` provider.
 *
 * Microsoft-managed agents don't carry endpoint / auth / model information at the
 * connection layer — the runtime resolves the endpoint pool from ACA
 * environment variables (see R4) and enforces the curated model list
 * server-side (see R11a).
 *
 * **This reference is portal-side scaffolding only — it is NOT persisted to
 * `workflow.json` or `connections.json`.** `connectionStore.exportToConnectionsConfig`
 * skips Microsoft-managed refs. The runtime dispatches on the workflow
 * action's `agentModelType === 'MicrosoftManaged'`, without a modelConfigurations
 * connection reference.
 *
 * The ref exists in the in-memory `connectionStore.references` map so
 * the designer UI has something to bind to for the "which connection?"
 * choice on the action. `connectionProperties.type: 'managedModel'` is
 * the discriminator the UI reads (via `MANAGED_MODEL_CONNECTION_PROVIDER_TYPE`)
 * to know a ref represents Microsoft-managed.
 *
 * Consumers:
 *   - `AgentTypeView` — injects this ref into the connection
 *     store when a user picks Workflow / Managed Agent and Microsoft-managed is the
 *     only option (no BYOM connections exist in the workflow).
 *   - `ConnectionTab` — surfaces "Microsoft-managed (default)" as
 *     a picker row and swaps a workflow's model connection ref to
 *     `MANAGED_MODEL_REFERENCE_KEY`.
 *   - `DeploymentField` — detects Microsoft-managed via `connectionProperties.type`
 *     and swaps the deployment picker to a curated list backed by
 *     `useManagedModels`.
 */

import type { ConnectionReference } from '@/types/connectionReference';

/**
 * Fixed reference key for in-memory editor state. Never serialized to a workflow
 * action or connections.json.
 */
export const MANAGED_MODEL_REFERENCE_KEY = 'managedAiConnection';

/**
 * User-facing display name for the Microsoft-managed connection. Rendered anywhere
 * a connection would normally show its raw `connectionReferenceName`
 * (picker rows, node-editor connection header, error banner, etc.) so
 * users see the branded label instead of the internal ref key. Kept
 * as a single constant so PM-driven copy changes flow to every surface.
 */
export const MANAGED_MODEL_DISPLAY_NAME = 'Microsoft-managed';

/**
 * `connectionProperties.type` marker that identifies an in-memory
 * connection reference as Microsoft-managed. Used by
 * `connectionStore.exportToConnectionsConfig` to skip the ref during
 * serialization, and by UI code (`DeploymentField`, etc.) to swap to
 * Microsoft-managed-specific behaviour. Not written to `connections.json`.
 */
export const MANAGED_MODEL_CONNECTION_PROVIDER_TYPE = 'managedModel';

/**
 * Bare `api.id` for the synthetic Microsoft-managed reference. Aligns with the
 * canonical `AgentModelProvider` member `'MicrosoftManaged'` so the
 * `getAgentReferenceProvider` classifier resolves the ref correctly.
 */
export const MANAGED_MODEL_PROVIDER_ID = 'MicrosoftManaged';

/**
 * Build the minimal `ConnectionReference` for the Microsoft-managed agent connection.
 * Intentionally omits every optional field (`connectionName`,
 * `authentication`, `connectionRuntimeUrl`, etc.) — the ref only needs
 * enough shape to satisfy the UI's binding contract, since it never
 * reaches disk.
 */
export const buildManagedModelReference = (): ConnectionReference => ({
  api: { id: MANAGED_MODEL_PROVIDER_ID },
  connection: { id: MANAGED_MODEL_REFERENCE_KEY },
  connectionProperties: { type: MANAGED_MODEL_CONNECTION_PROVIDER_TYPE },
});
